import { useQuery } from "@apollo/client";
import { createAjv } from "@jsonforms/core";
import { useJsonForms, withJsonFormsControlProps } from "@jsonforms/react";
import { Button, Dropdown, LoadingSpinner, Modal } from "pepsico-ds";
import PropTypes from "prop-types";
import { useContext, useState } from "react";
import { ActivitySetupContext } from "../../../context/ActivitySetupContext";
import { CampaignSetupContext } from "../../../context/CampaignSetupContext";
import { matchKeywordGroupsList } from "../../../graphql/queries/listKeywordGroups";
import { useToast } from "../../../hooks/useToast";
import { capitalizeFirstLetterOfEachWord } from "../../../utils/commons";
import MatchKeywordsComponent from "../../campaign/ActivitySetup/keywordsGroup/MatchKeywordsComponent";
import ButtonGroupDynamic from "../../common/ButtonGroupDynamic";
import Table from "../../common/table/Table";
import ToastNotification from "../../common/toastNotification/ToastNotification";
import { ActivitySetupFormWrapper } from "../activitySetupFormsWrapper/ActivitySetupFormsWrapper";
import "./keyword-groups-quantity-selector.scss";

const DEFAULT_TITLE = "Keyword Groups";
const DEFAULT_FILTER_TYPE = "BRAND";
const SELECTION_STEP = 0;
const THRESHOLD_STEP = 1;

const THRESHOLD_UI_SCHEMA = {
  type: "Control",
  scope: "#/properties/quantity_threshold",
  label: "Quantity threshold",
  placeholder: "Minimum quantity (e.g. 1)",
  helperText:
    "Minimum quantity of qualifying items from this keyword group. The rule passes when any group meets its threshold.",
};

const ajv = createAjv();

const formatGroupType = (filterBy) =>
  filterBy ? capitalizeFirstLetterOfEachWord(filterBy) : "";

const createEmptyGroup = (filterBy) => ({
  filter_by: filterBy,
  ids: [],
  quantity_threshold: 1,
});

const getSelectionHeaderData = (filterBy) => [
  { header: formatGroupType(filterBy) || "Name", columnKey: "name" },
  { header: "ID", columnKey: "id" },
];

const getPrimaryText = (isEditing, currentStep) => {
  if (isEditing) {
    return "Save";
  }
  return currentStep === SELECTION_STEP ? "Next" : "Add group";
};

const GroupEntry = ({
  group,
  index,
  groupCount,
  onRemove,
  onEditSelection,
  onEditThreshold,
}) => {
  const { data, loading } = useQuery(matchKeywordGroupsList, {
    variables: { type: group.filter_by || DEFAULT_FILTER_TYPE },
    fetchPolicy: "cache-first",
  });

  const keywordGroups = data?.listKeywordGroups || [];
  const rows = keywordGroups
    .filter((kg) => group.ids?.includes(kg.id))
    .map((kg) => ({ name: kg.name, id: kg.id || "-" }));

  return (
    <div className="keyword-group-entry">
      <div className="keyword-group-entry-header">
        <span className="font-xsmall-bold">
          Group {index + 1} ({formatGroupType(group.filter_by)})
        </span>
        <ButtonGroupDynamic
          size="small"
          items={[
            {
              icon: "delete",
              disabled: groupCount === 1,
              onClick: () => onRemove(index),
            },
          ]}
        />
      </div>
      <div className="keyword-groups-table-container">
        {loading ? (
          <div className="loading-container">
            <LoadingSpinner size={{ height: "2rem", width: "2rem" }} />
          </div>
        ) : (
          <>
            <Table
              headerData={getSelectionHeaderData(group.filter_by)}
              rowData={rows}
              pagination={true}
              rowsPerPage={10}
            />
            <ButtonGroupDynamic
              size="small"
              items={[{ icon: "edit", onClick: () => onEditSelection(index) }]}
            />
          </>
        )}
      </div>
      <div className="keyword-group-threshold-container">
        <div className="keyword-group-threshold-labels">
          <span className="font-xsmall-bold">
            Group {index + 1}: Quantity threshold
          </span>
          <span className="font-xsmall-regular">{group.quantity_threshold}</span>
        </div>
        <ButtonGroupDynamic
          size="small"
          items={[{ icon: "edit", onClick: () => onEditThreshold(index) }]}
        />
      </div>
    </div>
  );
};

GroupEntry.propTypes = {
  group: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  groupCount: PropTypes.number.isRequired,
  onRemove: PropTypes.func.isRequired,
  onEditSelection: PropTypes.func.isRequired,
  onEditThreshold: PropTypes.func.isRequired,
};

const KeywordGroupsQuantitySelectorComponent = ({
  data,
  path,
  uischema,
  handleChange,
}) => {
  const { currentCampaignTypeConfig } = useContext(CampaignSetupContext);
  const { activity, removeRule } = useContext(ActivitySetupContext);
  const { toast, showToast, closeToast } = useToast();

  const {
    core: { schema: coreSchema, uischema: coreUiSchema },
  } = useJsonForms();

  const groupSchema = coreSchema?.properties?.groups?.items;
  const title = uischema.title || DEFAULT_TITLE;

  const filterTypes = (groupSchema?.properties?.filter_by?.enum || []).map((id) => ({
    id,
    displayText: formatGroupType(id),
  }));
  const defaultFilterType = filterTypes[0]?.id;

  const groups = Array.isArray(data) ? data : [];

  const [showModal, setShowModal] = useState(!data || groups.length === 0);
  const [editingIndex, setEditingIndex] = useState(null);
  const [currentStep, setCurrentStep] = useState(SELECTION_STEP);
  const [modalGroup, setModalGroup] = useState(createEmptyGroup(defaultFilterType));
  const [thresholdFormData, setThresholdFormData] = useState({});
  const [savedData, setSavedData] = useState(data);

  const isEditing = editingIndex !== null;

  const toggleModal = () => setShowModal(!showModal);

  const goToStep = (step, group) => {
    if (step === THRESHOLD_STEP) {
      setThresholdFormData({ quantity_threshold: group.quantity_threshold });
    }
    setCurrentStep(step);
  };

  const handleAddGroup = () => {
    setEditingIndex(null);
    setModalGroup(createEmptyGroup(defaultFilterType));
    goToStep(SELECTION_STEP);
    setShowModal(true);
  };

  const handleEditGroup = (idx, step) => {
    setEditingIndex(idx);
    setModalGroup({ ...groups[idx] });
    goToStep(step, groups[idx]);
    setShowModal(true);
  };

  const handleRemoveGroup = (idx) => {
    const updatedGroups = groups.filter((_, i) => i !== idx);
    handleChange(path, updatedGroups);
    setSavedData(updatedGroups);
    showToast("success", "Keyword group successfully removed", "");
  };

  const handleCancel = () => {
    if (!savedData && groups.length === 0) {
      removeRule(coreUiSchema.ruleSlug, coreUiSchema.occurrence);
      toggleModal();
      return;
    }
    if (savedData) {
      handleChange(path, savedData);
    }
    toggleModal();
  };

  const handlePreviousStep = () => setCurrentStep(SELECTION_STEP);

  const handleFilterTypeChange = (values) => {
    const newType =
      Array.isArray(values) && values.length ? values[0].id : defaultFilterType;
    setModalGroup((prev) => ({
      ...prev,
      filter_by: newType,
      ids: [],
    }));
  };

  const handleIdsChange = (newIds) => {
    setModalGroup((prev) => ({
      ...prev,
      ids: newIds,
    }));
  };

  const handleThresholdChange = (formData) => {
    const threshold = formData?.quantity_threshold;
    setModalGroup((prev) =>
      prev.quantity_threshold === threshold
        ? prev
        : { ...prev, quantity_threshold: threshold }
    );
  };

  const handleSaveGroup = () => {
    if (!modalGroup.ids || modalGroup.ids.length === 0) {
      showToast("error", "Please select at least one keyword group", "");
      return;
    }

    if (!isEditing && currentStep === SELECTION_STEP) {
      goToStep(THRESHOLD_STEP, modalGroup);
      return;
    }

    if (!modalGroup.quantity_threshold || modalGroup.quantity_threshold < 1) {
      showToast("error", "Quantity threshold must be at least 1", "");
      return;
    }

    if (groupSchema && !ajv.validate(groupSchema, modalGroup)) {
      showToast("error", "Please fill all the required fields", "");
      return;
    }

    const updatedGroups = isEditing
      ? groups.map((g, idx) => (idx === editingIndex ? modalGroup : g))
      : [...groups, modalGroup];

    handleChange(path, updatedGroups);
    setSavedData(updatedGroups);
    toggleModal();
    showToast(
      "success",
      isEditing
        ? "Keyword group successfully updated"
        : "Keyword group successfully added to activity",
      ""
    );
  };

  const onRemoveSet = () => {
    removeRule(coreUiSchema.ruleSlug, coreUiSchema.occurrence);
  };

  const isOnlyOccurrence =
    activity?.activityRules?.filter(
      (rule) => rule.ruleDefinition.slug === coreUiSchema.ruleSlug
    ).length === 1;

  const rulesConfiguration =
    activity.activityDefinition.rulesConfiguration ||
    currentCampaignTypeConfig.requirement.possibleActivities.find(
      (a) => a.id === activity.activityDefinition.id
    ).rulesConfiguration;

  const isRequiredConfiguration =
    rulesConfiguration?.rules.find((rule) => rule.rule?.slug === uischema.ruleSlug)
      ?.minRequired > 0;

  const isRequired =
    isRequiredConfiguration ||
    activity.activityDefinition.requiredRules?.includes(coreUiSchema.ruleSlug);

  const showsPreviousStep = !isEditing && currentStep === THRESHOLD_STEP;

  return (
    <div className="keyword-groups-quantity-sets">
      {savedData && groups.length > 0 && (
        <div
          className="keyword-groups-details"
          key={`${coreUiSchema.ruleSlug}-${coreUiSchema.occurrence}`}
        >
          <div className="keyword-groups-header">
            <span className="font-body-bold">
              {title} ({groups.length})
            </span>
            <ButtonGroupDynamic
              size="small"
              items={[
                {
                  icon: "delete",
                  disabled: isOnlyOccurrence && isRequired,
                  onClick: onRemoveSet,
                },
              ]}
            />
          </div>
          {groups.map((group, idx) => (
            <GroupEntry
              key={idx}
              group={group}
              index={idx}
              groupCount={groups.length}
              onRemove={handleRemoveGroup}
              onEditSelection={(i) => handleEditGroup(i, SELECTION_STEP)}
              onEditThreshold={(i) => handleEditGroup(i, THRESHOLD_STEP)}
            />
          ))}
          <Button
            size="small"
            variant="tertiary"
            text="+ Add group"
            onClick={handleAddGroup}
          />
        </div>
      )}
      <Modal
        className="keyword-groups-rule-setup-modal"
        showModal={showModal}
        onCloseModal={handleCancel}
        title={`${isEditing ? "Edit" : "Add"} group - ${title}`}
        showTertiaryButton={false}
        showLink={false}
        secondaryButtonProps={{
          size: "medium",
          text: showsPreviousStep ? "Previous" : "Cancel",
          variant: "tertiary",
          onClick: showsPreviousStep ? handlePreviousStep : handleCancel,
        }}
        primaryButtonProps={{
          size: "medium",
          text: getPrimaryText(isEditing, currentStep),
          variant: "primary",
          onClick: handleSaveGroup,
        }}
      >
        <div className="w-100 col p-0 overflow-y-auto">
          {currentStep === SELECTION_STEP && (
            <div>
              <div className="group-type-selector">
                <Dropdown
                  childList={filterTypes}
                  selection="single"
                  label="Group type"
                  isRequired={true}
                  selectedValue={filterTypes.filter(
                    (t) => t.id === modalGroup.filter_by
                  )}
                  setSelectedValue={handleFilterTypeChange}
                  placeholder="Select group type"
                />
              </div>
              <MatchKeywordsComponent
                key={modalGroup.filter_by}
                selectedIds={modalGroup.ids}
                setSelectedIds={handleIdsChange}
                filterBy={modalGroup.filter_by || DEFAULT_FILTER_TYPE}
                required
                enableSelectAll
                hideHeader
              />
            </div>
          )}
          {currentStep === THRESHOLD_STEP && (
            <ActivitySetupFormWrapper
              jsonschema={groupSchema}
              uischema={THRESHOLD_UI_SCHEMA}
              data={thresholdFormData}
              onChange={handleThresholdChange}
            />
          )}
        </div>
        <ToastNotification {...toast} handleClose={closeToast} />
      </Modal>
      {!showModal && <ToastNotification {...toast} handleClose={closeToast} />}
    </div>
  );
};

export const KeywordGroupsQuantitySelectorControl = withJsonFormsControlProps(
  KeywordGroupsQuantitySelectorComponent
);

export default KeywordGroupsQuantitySelectorControl;
